/**
 * Fontes públicas: o que se sabe desta ficha em fonte aberta — e o que ainda não se perguntou.
 *
 * O painel tem duas metades, e a de cima é a que justifica ele existir. Ela mostra **todas** as
 * fontes do catálogo, inclusive as que não podem ser consultadas, com o motivo escrito e o
 * endereço onde se obtém a credencial. Uma tela que listasse só as menções encontradas não teria
 * como distinguir «não aparece em diário nenhum» de «ninguém perguntou» — e é olhando esta tela
 * que alguém decide parar de procurar.
 *
 * Por isso cada fonte diz uma de quatro coisas, nunca um silêncio: nunca consultada · consultada
 * em tal dia e nada encontrado · consultada, N documentos · indisponível, e por quê.
 *
 * A metade de baixo são as menções. Cada uma carrega o trecho e o LINK do documento publicado,
 * porque menção não é fato sobre a pessoa — é fato sobre um papel. O chip da chave diz como ela
 * foi encontrada: por CPF/CNPJ é a própria pessoa; por nome é candidato, e a tela usa essa
 * palavra, porque 103 sobrenomes carregam 60% das 71 mil pessoas da base.
 *
 * Nada aqui escreve na ficha. Adotar um dado é outro ato, do operador.
 */
import { AlertTriangle, ExternalLink, FileSearch, KeyRound, Search } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Chip, Separator, Spinner } from '@/components/ui/misc';
import { api } from '@/lib/api';

type Consulta = {
  chave: string;
  termo: string;
  sucesso: boolean;
  mensagem: string | null;
  encontrados: number;
  /** Quantos a fonte diz TER. Maior que `encontrados` = a página pedida cortou o resto. */
  total_na_fonte: number | null;
  consultado_em: string;
};

type Fonte = {
  id: string;
  nome: string;
  descricao: string;
  chaves: string[];
  gratuita: boolean;
  precisa_credencial: boolean;
  url_credencial: string | null;
  disponivel: boolean;
  motivo: string;
  ultima_consulta: Consulta | null;
};

type Mencao = {
  id: string;
  fonte: string;
  chave: string;
  termo: string;
  titulo: string;
  trecho: string | null;
  url: string | null;
  data_documento: string | null;
};

type Estado = {
  entidade: { tipo: string; id: string; nome: string };
  termos: Record<string, string[]>;
  fontes: Fonte[];
  mencoes: Mencao[];
};

type Resultado = {
  fonte: string;
  chaves: string[];
  consultada: boolean;
  sucesso: boolean;
  mensagem: string;
  encontrados: number;
  total_na_fonte: number | null;
  novos: number;
};

type Relatorio = {
  encontrados: number;
  novos: number;
  falhas: number;
  resultados: Resultado[];
};

/** Data pura não passa pelo `Date`: `new Date('2024-03-01')` é meia-noite UTC e exibe 29/02. */
function soData(iso: string): string {
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

function quando(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR');
}

/** O que esta fonte tem a dizer sobre si mesma agora — uma de quatro frases, nunca o silêncio. */
function EstadoDaFonte({ f }: { f: Fonte }) {
  if (!f.disponivel) {
    return (
      <p className="tipo-corpo-pequeno flex flex-wrap items-center gap-1 text-warning">
        <KeyRound className="size-3" /> {f.motivo}
        {f.url_credencial && (
          <a className="underline" href={f.url_credencial} target="_blank" rel="noreferrer">
            obter a chave
          </a>
        )}
      </p>
    );
  }
  const c = f.ultima_consulta;
  if (!c) return <p className="tipo-corpo-pequeno text-text-tertiary">Nunca consultada.</p>;
  if (!c.sucesso) {
    return (
      <p className="tipo-corpo-pequeno text-danger">
        A consulta de {quando(c.consultado_em)} falhou — {c.mensagem}
      </p>
    );
  }
  // «7 de 47» e não «7»: a fonte é paginada e nós pedimos uma página. Dizer só o que voltou,
  // tendo em mãos o total, é afirmar «são estes» sobre o que nunca se viu.
  const cortou = c.total_na_fonte != null && c.total_na_fonte > c.encontrados;
  const quantos =
    c.encontrados === 0
      ? 'nada encontrado'
      : `${c.encontrados} documento${c.encontrados > 1 ? 's' : ''}`;
  return (
    <p className="tipo-corpo-pequeno text-text-tertiary">
      Consultada em {quando(c.consultado_em)}: {quantos}
      {cortou && (
        <span className="text-warning">
          {' '}
          — a fonte tem {c.total_na_fonte}; consultar de novo não traz mais
        </span>
      )}{' '}
      · <span title="O termo exatamente como foi enviado à fonte">termo «{c.termo}»</span>
    </p>
  );
}

function LinhaMencao({ m }: { m: Mencao }) {
  return (
    <li className="border-b border-border py-3 last:border-0">
      <div className="flex flex-wrap items-center gap-2">
        <p className="tipo-corpo">{m.titulo}</p>
        {m.data_documento && (
          <span className="tipo-corpo-pequeno text-text-tertiary">{soData(m.data_documento)}</span>
        )}
        {m.chave === 'nome' ? (
          // Achado por nome é CANDIDATO, e a tela precisa dizer essa palavra.
          <Chip className="gap-1 border-warning/40 text-warning">
            <AlertTriangle className="size-3" /> achado por nome — pode ser homônimo
          </Chip>
        ) : (
          <Chip className="border-success/40 text-success">
            achado pelo {m.chave.toUpperCase()}
          </Chip>
        )}
      </div>
      {m.trecho && (
        <p className="tipo-corpo-pequeno mt-1 whitespace-pre-wrap text-text-secondary">
          {m.trecho}
        </p>
      )}
      {m.url && (
        <a
          className="tipo-corpo-pequeno mt-1 inline-flex items-center gap-1 text-accent underline"
          href={m.url}
          target="_blank"
          rel="noreferrer"
        >
          <ExternalLink className="size-3" /> abrir o documento publicado
        </a>
      )}
    </li>
  );
}

export function PainelFontesPublicas({
  item,
  tipo,
}: {
  item: { id: string };
  /** O `owner_tipo` da ficha, vindo do descritor do recurso e não de um palpite daqui: o mesmo
   *  painel serve pessoa e empresa, e as fontes de cada uma são diferentes. */
  tipo: string;
}) {
  const [estado, setEstado] = useState<Estado | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [consultando, setConsultando] = useState(false);
  const [incluirNome, setIncluirNome] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    const r = await api.get<Estado>(`/cadastro/fontes/${tipo}/${item.id}`);
    setCarregando(false);
    if (r.ok) setEstado(r.dados);
    else toast.error(r.erro);
  }, [item.id, tipo]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const consultar = async (fontes?: string[]) => {
    setConsultando(true);
    const r = await api.post<Relatorio>(`/cadastro/fontes/${tipo}/${item.id}`, {
      fontes: fontes ?? [],
      incluir_nome: incluirNome,
    });
    setConsultando(false);
    if (!r.ok) {
      toast.error(r.erro);
      return;
    }
    // A falha de UMA fonte é resultado, não erro da requisição: ela é dita, uma por uma, porque o
    // operador precisa saber de qual lado veio o silêncio.
    for (const res of r.dados.resultados.filter((x) => x.consultada && !x.sucesso)) {
      toast.error(`${res.fonte}: ${res.mensagem}`);
    }
    toast.success(
      r.dados.novos > 0
        ? `${r.dados.novos} documento(s) novo(s) de ${r.dados.encontrados} encontrado(s).`
        : `${r.dados.encontrados} documento(s) encontrado(s), nenhum novo.`
    );
    await carregar();
  };

  if (carregando) return <Spinner />;
  if (!estado) return null;

  const consultaveis = estado.fontes.filter((f) => f.disponivel);
  const termos = Object.entries(estado.termos)
    .map(([chave, valores]) => `${chave} «${valores[0]}»`)
    .join(' · ');

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2">
          <FileSearch className="size-4" /> Fontes públicas
        </CardTitle>
        <div className="flex flex-wrap items-center gap-3">
          {/* Busca por nome é opcional e nasce desligada: ela acha o homônimo com a mesma
              facilidade com que acha a pessoa, e quem assume esse risco tem de pedir por ele. */}
          <label className="tipo-corpo-pequeno flex items-center gap-2 text-text-secondary">
            <input
              type="checkbox"
              checked={incluirNome}
              onChange={(e) => setIncluirNome(e.target.checked)}
            />
            buscar também pelo nome (traz homônimo)
          </label>
          <Button
            size="sm"
            disabled={consultando || consultaveis.length === 0}
            onClick={() => void consultar()}
          >
            {consultando ? <Spinner className="size-4" /> : <Search className="size-4" />}
            Consultar {consultaveis.length} fonte{consultaveis.length === 1 ? '' : 's'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <ul className="mb-2">
          {estado.fontes.map((f) => (
            <li
              key={f.id}
              className="flex items-start justify-between gap-3 border-b border-border py-3 last:border-0"
            >
              <div className="min-w-0">
                <p className="tipo-corpo">
                  {f.nome}
                  <span className="tipo-corpo-pequeno text-text-tertiary">
                    {' '}
                    · por {f.chaves.join(', ').toUpperCase()}
                  </span>
                </p>
                <p className="tipo-corpo-pequeno text-text-secondary">{f.descricao}</p>
                <EstadoDaFonte f={f} />
              </div>
              <Button
                variant="secundario"
                size="sm"
                disabled={consultando || !f.disponivel}
                onClick={() => void consultar([f.id])}
              >
                Consultar
              </Button>
            </li>
          ))}
        </ul>

        {/* Os termos que saem daqui para fora, à vista: é o que permite refazer a busca à mão. */}
        <p className="tipo-corpo-pequeno text-text-tertiary">
          {termos
            ? `Termos de busca: ${termos}`
            : 'A ficha não tem documento nem nome para consultar.'}
        </p>

        <Separator className="my-4" />

        {estado.mencoes.length === 0 ? (
          <p className="tipo-corpo-pequeno text-text-tertiary">
            Nenhum documento público ligado a esta ficha ainda. Isso não quer dizer que não exista:
            veja acima quais fontes já foram consultadas.
          </p>
        ) : (
          <>
            <p className="tipo-corpo-pequeno mb-1 text-text-secondary">
              {estado.mencoes.length} documento(s) público(s). Nada disto está gravado na ficha —
              quem afirma é você.
            </p>
            <ul>
              {estado.mencoes.map((m) => (
                <LinhaMencao key={m.id} m={m} />
              ))}
            </ul>
          </>
        )}
      </CardContent>
    </Card>
  );
}
